import {stringify} from 'query-string';
import {withAdminBrokerToken, withAdminKolToken, withToken} from '@/config/white-list';
import {mc} from '@/core';
import {envUtil} from '@/helper/env';
import {genSystemDeviceForHttp} from '@/helper/device';
import {defaultSiteByHost, isLocalizedSite} from '@/helper/localized-site';
import {sd} from '@/da';
import {readToken, writeToken, removeToken, readAdminToken, readAdminBrokerToken} from '@/storage/token';
import {readBid} from '@/storage/bid';
import {readLang} from '@/storage/lang';
import {getMockTwitterHeader} from '@/modules/web3/social-trade/utils';

const TOKEN_HEADER = 'phemex-auth-token';
const TOKEN_ADMIN = 'admin-kol-token';
const TOKEN_BROKER = 'admin-broker-token';
const TOKEN_SITE = 'x-phemex-site';

export const http = {get, post, put, delete: del, upload};

function fillHeaders(xhr, {url, headers}) {
  xhr.setRequestHeader('bid', readBid());
  xhr.setRequestHeader('Accept-Language', readLang());
  if (envUtil.isApp) {
    xhr.setRequestHeader('x-phemex-device', genSystemDeviceForHttp());
  }
  if (isLocalizedSite) {
    xhr.setRequestHeader(TOKEN_SITE, defaultSiteByHost);
  }
  if (!envUtil.isProduction) {
    // 设置模拟twitter 账号 header key, 方便测试
    const mockHeader = getMockTwitterHeader();
    if (mockHeader) {
      xhr.setRequestHeader('x-phemex-auth-mock-profile-id', getMockTwitterHeader());
    }
  }
  if (withToken(url)) {
    const token = readToken();
    token && xhr.setRequestHeader(TOKEN_HEADER, token);
  }
  if (withAdminKolToken(url)) {
    const token = readAdminToken();
    token && xhr.setRequestHeader(TOKEN_ADMIN, token);
  }
  if (withAdminBrokerToken(url)) {
    const brokerToken = readAdminBrokerToken();
    brokerToken && xhr.setRequestHeader(TOKEN_BROKER, brokerToken);
  }
  headers && Object.keys(headers).forEach((key) => xhr.setRequestHeader(key, headers[key]));
}

function ajax(options) {
  options = options || {};

  let querystring = options.querystring || stringify(options.query);
  querystring = querystring ? '?' + querystring : '';

  const url = options.url + querystring;
  const body = options.formData ? options.formData : options.params ? JSON.stringify(options.params) : null;

  const xhr = window.XMLHttpRequest ? new XMLHttpRequest() : new ActiveXObject('Microsoft.XMLHTTP');

  xhr.responseType = options.responseType || '';

  xhr.onreadystatechange = () => {
    if (xhr.readyState === 2) {
      const token = xhr.getResponseHeader(TOKEN_HEADER);
      if (token) {
        writeToken(token);
        console.log('update TK');
      }
    }
    if (xhr.readyState === 4) {
      const status = xhr.status;
      if ((status >= 200 && status < 300) || status === 400) {
        if (xhr.responseType === 'blob') {
          const reply = xhr.response;
          if (reply) {
            if (options.success) {
              options.success(reply, reply);
            }
            return;
          }
          if (options.fail) {
            options.fail({code: 498, message: 'blob is empty'}, null);
            collectHttpError(url, reply.code, xhr);
            return;
          }
        }

        let reply = {};
        try {
          reply = JSON.parse(xhr.responseText);
        } catch (e) {
          reply = {
            status,
            code: 498,
            msg: 'json parse error',
          };
        }
        if (reply.code === 0) {
          if (options.success) {
            options.success(options.formatter ? options.formatter(reply.data) : reply.data, reply);
          }
          return;
        }

        if (options.fail) {
          options.fail({code: reply.code, message: reply.msg}, reply);
          collectHttpError(url, reply.code, xhr);
          return;
        }
      }

      if (status === 401) {
        const reply = JSON.parse(xhr.responseText);
        if (!envUtil.isApp) {
          removeToken();
        }
        mc.send('http', '401', {reply});
        options.fail({code: 401, message: 'Unauthorized'}, null);
        collectHttpError(url, reply.code, xhr);
        return;
      }

      if (status === 403) {
        options.fail({code: 403, message: 'Request denied'}, null);
        collectHttpError(url, 403, xhr);
        return;
      }

      if (status === 429) {
        const message = 'Too many requests, Please try again later';
        options.fail({code: status, message}, null);
        collectHttpError(url, status, xhr);
        mc.send('toast', 'error', {message});
        return;
      }

      if (options.fail) {
        let code = 499;
        try {
          const reply = JSON.parse(xhr.responseText);
          code = reply.code;
        } catch (e) {}
        console.warn('network error', 'status: ', status, 'code: ', code);
        options.fail({code, message: 'network error'}, null);
        collectHttpError(url, code, xhr);
      }
    }
  };

  xhr.open(options.type, url, true);
  fillHeaders(xhr, options);
  xhr.send(body);
}

function get(url, {query = {}, ...rest}) {
  return new Promise((resolve) =>
    ajax({
      url,
      type: 'GET',
      query,
      success(data, reply) {
        resolve({loading: false, success: true, data, reply});
      },
      fail(error, reply) {
        resolve({loading: false, fail: true, error, reply});
      },
      ...rest,
    })
  );
}

function post(url, {query = {}, params = {}, ...rest}) {
  const {fail, ...otherParams} = rest;
  return new Promise((resolve) =>
    ajax({
      url,
      type: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      query,
      params,
      success(data, reply) {
        resolve({loading: false, success: true, data, reply});
      },
      fail(error, reply) {
        fail && fail(error, reply);
        resolve({loading: false, fail: true, error, reply});
      },
      ...otherParams,
    })
  );
}

function put(url, {query, params, ...rest}) {
  return new Promise((resolve) =>
    ajax({
      url,
      type: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      query,
      params,
      success(data, reply) {
        resolve({loading: false, success: true, data, reply});
      },
      fail(error, reply) {
        resolve({loading: false, fail: true, error, reply});
      },
      ...rest,
    })
  );
}

function del(url, {query, params, ...rest}) {
  return new Promise((resolve) =>
    ajax({
      url,
      type: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      query,
      params,
      success(data, reply) {
        resolve({loading: false, success: true, data, reply});
      },
      fail(error, reply) {
        resolve({loading: false, fail: true, error, reply});
      },
      ...rest,
    })
  );
}

function upload(url, {formData}) {
  return new Promise((resolve) =>
    ajax({
      url,
      type: 'POST',
      formData,
      success(data, reply) {
        resolve({loading: false, success: true, data, reply});
      },
      fail(error, reply) {
        resolve({loading: false, fail: true, error, reply});
      },
    })
  );
}

function collectHttpError(url, code, {status, responseText}) {
  // 注册未完成错误码忽略（轮询操作）
  if (code === 31108) {
    return;
  }

  sd.track('web_debug', {debug_type: 'http_error', debug_info: {url, status, code, responseText}});
}
